import React, { useState, useEffect, useCallback, useRef } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Separator } from "@/components/ui/separator";
import { generateQuestionVideo, QuestionForVideo, VideoGenerationResult } from "@/services/videoGenerationService";
import { Loader2, Download, Video, X, AlertTriangle, RefreshCw } from "lucide-react";
import { clearVideoGenerationCaches } from "@/registerServiceWorker";

interface VideoGenerationModalProps {
  questions: QuestionForVideo[];
  open: boolean;
  onClose: () => void;
}

const VideoGenerationModal = ({ questions, open, onClose }: VideoGenerationModalProps) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [questionDuration, setQuestionDuration] = useState(30);
  const [answerDuration, setAnswerDuration] = useState(15);
  const [watermark, setWatermark] = useState("Practice Test");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [processedQuestions, setProcessedQuestions] = useState<QuestionForVideo[]>([]);
  const [videoKey, setVideoKey] = useState(0);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [blobObject, setBlobObject] = useState<Blob | null>(null);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [progressDisplay, setProgressDisplay] = useState<string>("0% complete");
  
  const lastProgressValueRef = useRef<number>(0);
  const progressValueHistory = useRef<Array<{time: number, value: number}>>([]);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const blobUrlRef = useRef<string | null>(null);
  const videoContainerRef = useRef<HTMLDivElement | null>(null);
  const isUnmountingRef = useRef<boolean>(false);
  const questionsCountRef = useRef<number>(0);

  const cleanupResources = useCallback(() => {
    console.log("Cleaning up video resources");
    
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      try {
        console.log("Stopping ongoing MediaRecorder");
        mediaRecorder.stop();
      } catch (e) {
        console.error("Error stopping MediaRecorder:", e);
      }
    }
    
    if (videoRef.current) {
      try {
        videoRef.current.pause();
        videoRef.current.src = "";
        videoRef.current.removeAttribute('src');
        videoRef.current.load();
        console.log("Video element cleaned");
      } catch (e) {
        console.error("Error cleaning up video element:", e);
      }
    }
    
    if (blobUrlRef.current && isUnmountingRef.current) {
      try {
        URL.revokeObjectURL(blobUrlRef.current);
        console.log("Revoked blob URL:", blobUrlRef.current);
        blobUrlRef.current = null;
      } catch (e) {
        console.error("Error revoking blob URL:", e);
      }
    }
    
    if (isUnmountingRef.current) {
      setBlobObject(null);
      setVideoUrl(null);
    }
  }, [mediaRecorder]);

  useEffect(() => {
    if (open) {
      console.log("VideoGenerationModal opened - clearing caches and resetting state");
      
      isUnmountingRef.current = false;
      
      setProgress(0);
      setProgressDisplay("0% complete");
      lastProgressValueRef.current = 0;
      progressValueHistory.current = [];
      
      setVideoError(null);
      setValidationError(null);
      setIsGenerating(false);
      
      clearVideoGenerationCaches();
      
      if (!questions || questions.length === 0) {
        console.warn("No questions provided to VideoGenerationModal");
        setValidationError("No questions selected");
        toast.warning("No questions selected", {
          description: "Please select at least one question to generate video"
        });
        setProcessedQuestions([]);
        return;
      }
      
      console.log(`VideoGenerationModal received ${questions.length} questions.`);
      questionsCountRef.current = questions.length;
      
      try {
        const questionsClone = JSON.parse(JSON.stringify(questions));
        
        console.log(`Deep cloned questions count: ${questionsClone.length}`);
        questionsClone.forEach((q, idx) => {
          console.log(`Question ${idx+1} (detail): ID=${q.id}, Text=${q.text.substring(0, 30)}..., Options=${q.options.length}, Has correct=${q.options.some(o => o.isCorrect)}`);
        });
        
        for (let i = 0; i < questionsClone.length; i++) {
          const q = questionsClone[i];
          console.log(`Validating question ${i+1}:`, q.id);
          
          if (!q.text) {
            setValidationError(`Question ${i + 1} is missing text content`);
            setProcessedQuestions([]);
            return;
          }
          if (!q.options || q.options.length === 0) {
            setValidationError(`Question ${i + 1} is missing options`);
            setProcessedQuestions([]);
            return;
          }
          if (!q.options.some(opt => opt.isCorrect)) {
            console.log(`Question ${i + 1} has no correct answer marked, marking first option as correct`);
            q.options[0].isCorrect = true;
          }
        }
        
        setProcessedQuestions(questionsClone);
        setValidationError(null);
        
        console.log(`Successfully processed and validated all ${questionsClone.length} questions for video generation`);
      } catch (error) {
        console.error("Error processing questions:", error);
        setValidationError("Error processing questions: " + (error instanceof Error ? error.message : String(error)));
        setProcessedQuestions([]);
      }
    } else {
      cleanupResources();
    }
  }, [questions, open, cleanupResources]);

  useEffect(() => {
    return () => {
      console.log("VideoGenerationModal unmounting - full cleanup");
      isUnmountingRef.current = true;
      cleanupResources();
    };
  }, [cleanupResources]);

  const handleForceClearCache = () => {
    isUnmountingRef.current = true;
    cleanupResources();
    isUnmountingRef.current = false;
    clearVideoGenerationCaches();
    toast.success("Cache cleared", {
      description: "All caches have been cleared for video generation"
    });
    setVideoUrl(null);
    setBlobObject(null);
    setProgress(0);
    setVideoError(null);
    
    progressValueHistory.current = [];
    lastProgressValueRef.current = 0;
  };

  const handleGenerateVideo = async () => {
    if (processedQuestions.length === 0) {
      toast.error("No valid questions available", {
        description: "Please select valid questions for video generation"
      });
      return;
    }
    
    try {
      setIsGenerating(true);
      
      setProgress(0);
      setProgressDisplay("0% complete");
      lastProgressValueRef.current = 0;
      progressValueHistory.current = [];
      
      setVideoError(null);
      
      if (mediaRecorder && mediaRecorder.state !== 'inactive') {
        try {
          mediaRecorder.stop();
        } catch (e) {
          console.error("Error stopping previous MediaRecorder:", e);
        }
      }
      
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.src = "";
        videoRef.current.load();
      }
      
      setValidationError(null);
      
      clearVideoGenerationCaches();
      
      console.log(`Starting video generation with ${processedQuestions.length} questions out of ${questionsCountRef.current} original`);
      console.log(`Question duration: ${questionDuration}s, Answer duration: ${answerDuration}s`);
      
      const questionsForVideo = JSON.parse(JSON.stringify(processedQuestions));
      
      console.log(`Sending ${questionsForVideo.length} questions to video generator (ALL questions, no limit):`);
      questionsForVideo.forEach((q, i) => {
        console.log(`Video question ${i+1}: ID=${q.id}, Text=${q.text.substring(0, 30)}...`);
      });
      
      questionsForVideo.forEach((q, i) => {
        if (!q.options.some(o => o.isCorrect)) {
          q.options[0].isCorrect = true;
          console.log(`Fixed question ${i+1}: No correct option was marked, set first option as correct`);
        }
      });
      
      if (questionsForVideo.length === 0) {
        throw new Error("No questions available for video generation");
      }
      
      const result = await generateQuestionVideo(
        {
          questions: questionsForVideo,
          questionDuration,
          answerDuration,
          watermark,
        },
        (progressValue) => {
          const now = Date.now();
          lastProgressValueRef.current = progressValue;
          progressValueHistory.current.push({time: now, value: progressValue});
          
          const validProgress = isNaN(progressValue) ? 0 : Math.min(100, Math.max(0, progressValue));
          
          setProgress(validProgress);
          
          const formattedProgress = `${Math.round(validProgress)}% complete`;
          setProgressDisplay(formattedProgress);
          
          if (progressValue % 10 === 0 || progressValue === 100) {
            console.log(`Video generation progress update: ${validProgress.toFixed(1)}% - display: ${formattedProgress}`);
          }
        },
        (recorder) => {
          setMediaRecorder(recorder);
        }
      );
      
      console.log("Video generation complete, Blob size:", result.blob?.size || "No blob returned");
      
      if (!result.url || !result.blob) {
        throw new Error("No video URL or blob returned from generation process");
      }
      
      blobUrlRef.current = result.url;
      setBlobObject(result.blob);
      
      console.log("Generated blob:", {
        type: result.blob.type,
        size: result.blob.size,
        url: result.url
      });
      
      progressValueHistory.current.push({time: Date.now(), value: 100});
      
      setTimeout(() => {
        setVideoKey(prev => prev + 1);
        setVideoUrl(result.url);
        console.log("Video URL and key updated, should display now");
      }, 1000);
      
      toast.success("Video generated successfully!");
    } catch (error) {
      console.error("Error generating video:", error);
      setVideoError(error instanceof Error ? error.message : "Unknown error");
      toast.error("Failed to generate video", {
        description: error instanceof Error ? error.message : "Unknown error"
      });
      setValidationError(error instanceof Error ? error.message : "Unknown error");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!blobObject) {
      toast.error("No video available", {
        description: "Could not download video because no blob is available. Try generating again."
      });
      return;
    }
    
    try {
      const blobUrl = URL.createObjectURL(blobObject);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `practice-questions-video-${new Date().toISOString().split("T")[0]}.webm`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
      }, 100);
      
      toast.success("Video download started");
    } catch (error) {
      console.error("Error downloading video:", error);
      toast.error("Download failed", {
        description: "Could not download the video. Try again or check console for errors."
      });
    }
  };

  const resetModal = () => {
    isUnmountingRef.current = false;
    onClose();
  };

  const handleVideoError = (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    console.error("Video playback error:", e);
    setVideoError("Video playback error. Try downloading the video to play it locally.");
    toast.error("Video playback error", {
      description: "There was an error playing the video. Try downloading it instead."
    });
  };

  const setVideoRef = (element: HTMLVideoElement | null) => {
    videoRef.current = element;
    
    if (element) {
      console.log("Video element ref set:", element);
    }
  };

  const totalVideoLengthMinutes = 
    ((questionDuration + answerDuration) * processedQuestions.length / 60).toFixed(1);

  const formattedProgressDisplay = isGenerating 
    ? progressDisplay 
    : progress > 0 ? `${Math.round(progress)}% complete` : '0% complete';

  return (
    <Dialog open={open} onOpenChange={resetModal}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Generate Question Video</DialogTitle>
          <DialogDescription>
            Create a kid-friendly video showing each question with timed answers and explanations.
          </DialogDescription>
        </DialogHeader>
        
        {!videoUrl ? (
          <>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="question-duration">Question Duration (seconds)</Label>
                  <Input
                    id="question-duration"
                    type="number"
                    value={questionDuration}
                    onChange={(e) => setQuestionDuration(Number(e.target.value))}
                    min={5}
                    max={120}
                  />
                </div>
                <div>
                  <Label htmlFor="answer-duration">Answer Duration (seconds)</Label>
                  <Input
                    id="answer-duration"
                    type="number"
                    value={answerDuration}
                    onChange={(e) => setAnswerDuration(Number(e.target.value))}
                    min={5}
                    max={120}
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="watermark">Watermark Text (optional)</Label>
                <Input
                  id="watermark"
                  value={watermark}
                  onChange={(e) => setWatermark(e.target.value)}
                  placeholder="Quiz App"
                />
              </div>
              
              <div className="py-2">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-500">
                    Total video length: {totalVideoLengthMinutes} minutes
                  </p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleForceClearCache}
                    title="Clear all browser caches to ensure fresh video generation"
                  >
                    <RefreshCw className="h-4 w-4 mr-1" />
                    Clear Cache
                  </Button>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Questions to include: {processedQuestions.length}
                </p>
                
                {processedQuestions.length > 0 && (
                  <div className="mt-2 text-xs bg-slate-50 p-2 rounded max-h-24 overflow-y-auto">
                    <p className="font-medium mb-1">Questions that will be included in video:</p>
                    <ol className="list-decimal list-inside">
                      {processedQuestions.map((q, idx) => (
                        <li key={q.id || idx} className="truncate">
                          {q.text.replace(/<[^>]*>/g, '').substring(0, 40)}...
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
                
                {(processedQuestions.length === 0 || validationError) && (
                  <div className="flex items-center gap-2 text-sm text-red-600 font-medium mt-2">
                    <AlertTriangle className="h-4 w-4" />
                    <p>{validationError || "No questions selected. Please select at least one question to generate a video."}</p>
                  </div>
                )}
              </div>
              
              <div className="space-y-2 mt-2">
                <p className="text-sm font-medium text-gray-700 mb-1">Generation Progress:</p>
                
                <div className="w-full flex flex-col">
                  <div className="w-full h-4 rounded-full bg-gray-100 overflow-hidden">
                    <Progress value={progress} className="w-full h-full transition-all" />
                  </div>
                  <p className="text-sm text-center text-gray-500 mt-1">
                    {formattedProgressDisplay}
                  </p>
                  {isGenerating && progress === 0 && (
                    <p className="text-xs text-amber-600 mt-1 text-center">
                      Progress will update when generation starts...
                    </p>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex justify-between">
              <Button variant="outline" onClick={resetModal} disabled={isGenerating}>
                <X className="mr-2 h-4 w-4" />
                Cancel
              </Button>
              <Button 
                onClick={handleGenerateVideo} 
                disabled={isGenerating || processedQuestions.length === 0 || !!validationError}
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Video className="mr-2 h-4 w-4" />
                    Generate Video
                  </>
                )}
              </Button>
            </div>
          </>
        ) : (
          <div className="space-y-4">
            <Separator />
            
            <div 
              className="aspect-video w-full bg-black relative" 
              ref={videoContainerRef}
            >
              <video 
                key={videoKey}
                ref={setVideoRef}
                src={videoUrl} 
                controls 
                className="w-full h-full" 
                controlsList="nodownload"
                autoPlay
                playsInline
                preload="auto"
                onError={handleVideoError}
              />
              
              {videoError && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900 bg-opacity-75 text-white p-4">
                  <AlertTriangle className="h-10 w-10 text-yellow-400 mb-2" />
                  <p className="text-center mb-4">{videoError}</p>
                  <Button 
                    onClick={handleDownload} 
                    variant="outline" 
                    className="bg-transparent hover:bg-white hover:text-gray-900 border-white text-white"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Download Video Instead
                  </Button>
                </div>
              )}
            </div>
            
            <div className="flex flex-col gap-2">
              <div className="text-sm text-gray-500">
                If the video doesn't play in the browser, you can download it and play it locally.
              </div>
              
              <div className="flex justify-between">
                <Button variant="outline" onClick={resetModal}>
                  <X className="mr-2 h-4 w-4" />
                  Close
                </Button>
                <Button 
                  onClick={handleDownload} 
                  variant="default"
                  title="Download the video to watch it locally"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download Video
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VideoGenerationModal;
